<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6">
      <card-title-row is-subtitle :straightTitle="title" class="pt-5 pb-2" />
      <sidebar-row has-icon-column border-bottom v-if="collisionsLoading">
        <main-loader class="pb-5" />
      </sidebar-row>
      <sidebar-row has-icon-column border-bottom v-else paddingRight="">
        <v-tabs v-model="currentTab" hide-slider class="custom-tabs" :show-arrows="false">
          <v-col cols="auto" class="px-0 pr-4" v-for="tab in computedTabs" :key="tab.value">
            <v-badge
              overlap
              :content="computedCollisionsTreesCounts[tab.scope]"
              :value="computedCollisionsTreesCounts[tab.scope] && computedCollisionsTreesCounts[tab.scope].length"
              color="error"
            >
              <v-tab v-text="tab.text" active-class="activeTab"> </v-tab>
            </v-badge>
          </v-col>
        </v-tabs>
        <v-row no-gutters class="pb-7">
          <v-col class="pa-0">
            <v-tabs-items v-model="currentTab">
              <v-tab-item v-for="tab in computedTabs" :key="tab.value">
                <v-row no-gutters>
                  <v-col cols="12" class="text-left">
                    <v-treeview :items="computedCollisionsTrees[tab.scope]">
                      <template #label="data">
                        <v-row no-gutters>
                          <v-col cols="10">
                            <v-row no-gutters v-on="getListeners(data)">
                              <v-col cols="auto" class="text-body-2">
                                {{ data.item.name }}
                              </v-col>
                            </v-row>
                            <v-row no-gutters v-if="data.item.subName">
                              <v-col
                                cols="auto"
                                class="font-italic font-weight-light italic--text"
                                style="font-size: 12px"
                              >
                                {{ data.item.subName }}
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-spacer />
                          <v-col cols="1" class="text-right" v-if="data.item.geometry">
                            <v-icon size="18" @click="zoomToFeature(data.item)"> mdi-magnify </v-icon>
                          </v-col>
                        </v-row>
                      </template>
                    </v-treeview>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
        <v-row no-gutters class="pr-5 pb-5" align="center" v-if="!hideBuffer">
          <v-col class="pa-0 mr-7" cols="5">
            <data-input v-model="bufferValue" v-bind="bufferInput" />
          </v-col>
          <v-col class="pa-0" cols="auto">
            <base-button
              class="text-body-2"
              translation-path="sidebar.watSewCollisionsInfo.check"
              @click="getCollisions(layerId, featureId, bufferValue)"
              :loading="collisionsBufferLoading"
              :disabled="!bufferValue"
            />
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import BaseButton from '@/components/BaseButton';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'WatSewCollisionsInfo',
  components: { BaseButton, CardTitleRow, DataInput, MainLoader, SidebarRow },
  props: {
    customCollisionsTrees: {
      type: Object,
    },
    customCollisionsTreesCounts: {
      type: Object,
    },
    hideBuffer: {
      type: Boolean,
      default: false,
    },
    hoverColor: {
      type: String,
      default: '35,119,234',
    },
    layerId: {
      type: [Number, String],
      default: null,
    },
    featureId: {
      type: [Number, String],
      default: null,
    },
    tabs: {
      type: Array,
      default: () => {
        return [
          {
            value: 'water',
            moduleName: 'WATER_DATA',
            settingName: 'water_data_module_enabled',
            scope: 'water',
          },
          {
            value: 'sewer',
            moduleName: 'SEWER_DATA',
            settingName: 'sewer_data_module_enabled',
            scope: 'sewer',
          },
        ];
      },
    },
    titlePath: {
      type: String,
      default: 'sidebar.watSewCollisionsInfo.watSewCollisionsTitle',
    },
    titleStraight: {
      type: String,
    },
  },
  computed: {
    additionalModules: get('admin/additionalModules'),
    dataSources: get('layers/metadata'),
    permissions: get('users/toolsPermissions'),
    settings: get('admin/settingsValues'),

    computedCollisionsTrees() {
      return this.customCollisionsTrees || this.collisionsTrees;
    },
    computedCollisionsTreesCounts() {
      return this.customCollisionsTreesCounts || this.collisionsTreesCounts;
    },

    bufferInput() {
      return {
        dataType: { name: 'float' },
        hideDetails: true,
        label: this.$i18n.t('sidebar.watSewCollisionsInfo.bufferLabel'),
        disabled: this.collisionsBufferLoading,
        loading: this.collisionsBufferLoading,
        allowNegative: false,
      };
    },
    computedTabs() {
      return this.tabs
        .filter(tab => this.isTabEnabled(tab))
        .map(tab => {
          const { value, scope } = tab;
          return {
            value,
            text: this.$i18n.t(`sidebar.watSewCollisionsInfo.tabsNames.${value}`),
            scope,
          };
        });
    },
    tabsScopes() {
      return this.tabs.map(tab => tab.scope);
    },

    title() {
      return this.titleStraight || this.$i18n.t(this.titlePath);
    },
  },
  data: () => ({
    bufferValue: null,
    currentTab: null,
    collisionsLoading: false,
    collisionsBufferLoading: false,
    collisionsTrees: {},
    collisionsTreesCounts: {},
  }),
  methods: {
    getWatSewCollisions: call('layers/getWatSewCollisions'),
    async getCollisions(layerId = this.layerId, featureId = this.featureId, bufferValue = this.bufferValue) {
      if ((!layerId && layerId !== 0) || (!featureId && featureId !== 0)) {
        return;
      }
      this[bufferValue ? 'collisionsBufferLoading' : 'collisionsLoading'] = true;
      try {
        const data = { layerId, featureId, bufferValue };
        const collisions = await this.getWatSewCollisions(data);
        if (bufferValue) {
          let features = [];
          Object.values(collisions).forEach((collisionsFeatures, cIdx) => {
            features = features.concat(
              collisionsFeatures.map((feature, fIdx) => {
                return {
                  type: 'Feature',
                  id: `${cIdx}-${fIdx}`,
                  properties: {},
                  geometry: feature.geometry,
                };
              })
            );
          });
          const geojson = {
            type: 'FeatureCollection',
            crs: {
              properties: {
                name: this.$_config.defaultEpsg,
              },
              type: 'name',
            },
            features,
          };
          this.$root.$emit('toggleWatSewCollisionBuffer', geojson);
        } else {
          const { collisionsTrees, collisionsTreesCounts } = this.getCollisionsTreeAndCounts(collisions);
          this.collisionsTrees = collisionsTrees;
          this.collisionsTreesCounts = collisionsTreesCounts;
        }
        this[bufferValue ? 'collisionsBufferLoading' : 'collisionsLoading'] = false;
      } catch {
        this[bufferValue ? 'collisionsBufferLoading' : 'collisionsLoading'] = false;
      }
    },
    getCollisionsTreeAndCounts(data = {}) {
      const collisionsTrees = {};
      const collisionsTreesCounts = {};
      this.tabsScopes.forEach(tab => {
        collisionsTrees[tab] = [];
        collisionsTreesCounts[tab] = 0;
      });
      Object.keys(data).forEach(dataSourceName => {
        const dataSource = this.dataSources[dataSourceName];
        const { scope } = dataSource;
        if (this.tabsScopes.includes(scope)) {
          const dataSourceTreeElement = {
            id: dataSourceName,
            name: dataSource.verbose_name,
            children: data[dataSourceName].map((feature, idx) => {
              const { name, attribute_name, area, length, geometry } = feature;
              let subName = '';
              if (area || area === 0) {
                subName = this.$i18n.t('sidebar.watSewCollisionsInfo.area', { value: area.toFixed(3) });
              } else if (length || length === 0) {
                subName = this.$i18n.t('sidebar.watSewCollisionsInfo.length', { value: length.toFixed(3) });
              }
              const attributeName = dataSource.attributes_schema.attributes.find(
                attribute => attribute.name === attribute_name
              )?.verbose_name;
              return {
                id: `${name}-${idx}`,
                name: `${attributeName}: ${name}`,
                subName,
                geometry,
              };
            }),
          };
          collisionsTrees[scope].push(dataSourceTreeElement);
          collisionsTreesCounts[scope] += dataSourceTreeElement?.children.length || 0;
        }
      });
      return { collisionsTrees, collisionsTreesCounts };
    },
    isTabEnabled(tab) {
      const { moduleName, settingName } = tab;
      const {
        [moduleName]: { enabled: superAdminEnabled, configured },
      } = this.additionalModules;
      const { [settingName]: adminEnabled } = this.settings;
      const { [moduleName]: { main_value: modulePermissions } = {} } = this.permissions;
      return superAdminEnabled && adminEnabled && configured && modulePermissions > 0;
    },
    getListeners(data) {
      const { leaf = false } = data;
      if (!leaf) return {};
      return {
        mouseleave: () => {
          this.onHover(data, false);
        },
        mouseenter: () => {
          this.onHover(data, true);
        },
      };
    },
    onHover(data, value) {
      this.$root.$emit('toggleWatSewCollisionHover', data.item, { value, rgbColor: this.hoverColor });
    },
    zoomToFeature(item) {
      const geometry = JSON.parse(JSON.stringify(item.geometry));
      geometry.crs = { properties: { name: this.$_config.defaultEpsg }, type: 'name' };
      this.$root.$emit('createGeomAndFitView', geometry);
    },
  },
  mounted() {
    this.getCollisions();
  },
  beforeDestroy() {
    this.$root.$emit('toggleWatSewCollisionBuffer');
  },
};
</script>
<style lang="scss" scoped>
.v-tab {
  letter-spacing: 0.0178571429em !important;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  height: fit-content;
  text-transform: unset !important;
}
.activeTab {
  background-color: map-get($colors, 'primarybackground');
}
.custom-tabs {
  position: sticky;
  z-index: 1;
}
.v-tab:not(.activeTab) {
  color: #5f6368 !important;
}
::v-deep {
  .v-slide-group__prev {
    display: none !important;
  }
  .v-slide-group__next {
    display: none !important;
  }
}
</style>
